import { Button, Dropdown, Menu, Row, Space, Tooltip } from 'antd';
import { ICompanyGroup } from 'app/containers/Company/slice/types';
import { FC } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { TextRegular14 } from 'styles/typography';
import { useGetAllCompanyGroupsQuery } from 'app/api';
import { useSelector } from 'react-redux';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectIsConsultant } from 'app/containers/Authorization/slice/selectors';
import { MoreOutlined } from '@ant-design/icons';
import { StyledTable } from 'app/components/StyledTable/StyledTable';

interface IProps {
  search: string;
  onGroupActivate: (groupId: number) => void;
  onCreateNewGroup: () => void;
  onGroupEdit: (companyGroup: ICompanyGroup) => void;
  onGroupArchive: (groupId: number) => void;
}

const CompanyGroupsTable: FC<IProps> = ({
  search,
  onGroupActivate,
  onCreateNewGroup,
  onGroupEdit,
  onGroupArchive,
}) => {
  const { t } = useTranslation();
  const envId = useSelector(selectSelectedEnvId) as number;
  const isConsultant = useSelector(selectIsConsultant);

  const {
    currentData: companyGroups,
    isFetching,
  } = useGetAllCompanyGroupsQuery({ envId }, { skip: !isConsultant });

  const filteredCompanyGroups = companyGroups?.filter(
    companyGroup =>
      companyGroup.name.toLowerCase().indexOf(search.toLowerCase()) > -1,
  );

  const columns: ColumnsType<ICompanyGroup> = [
    {
      title: t('Company.CompanyGroups', 'CompanyGroups'),
      dataIndex: 'name',
      render: (_: unknown, companyGroup) => companyGroup.name,
      sorter: {
        compare: (a, b) => {
          return a.name.localeCompare(b.name);
        },
      },
      width: '50%',
    },
    {
      title: t('Company.Companies', 'Companies'),
      dataIndex: 'name',
      render: (_: unknown, companyGroup) => (
        <Tooltip
          title={
            <>
              {companyGroup.companies.map(company => (
                <div key={company.id}>{company.name}</div>
              ))}
            </>
          }
          placement="right"
        >
          <span style={{ padding: '5px 10px' }}>
            {companyGroup.companies.length}
          </span>
        </Tooltip>
      ),
      sorter: {
        compare: (a, b) => {
          return a.companies.length - b.companies.length;
        },
      },
      width: '25%',
    },
    {
      title: t('Common.Actions', 'Actions'),
      dataIndex: 'actions',
      render: (_: unknown, companyGroup) => (
        <Space>
          <Button
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={() => onGroupActivate(companyGroup.id)}
          >
            {t('Company.Activate', 'Activate')}
          </Button>

          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item onClick={() => onGroupEdit(companyGroup)}>
                  {t('Common.Edit', 'Edit')}
                </Menu.Item>
                <Menu.Item onClick={() => onGroupArchive(companyGroup.id)}>
                  {t('Common.Archive', 'Archive')}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined style={{ fontSize: 16 }} />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row
        style={{
          margin: '19px 0 6px',
        }}
        justify="space-between"
      >
        <TextRegular14>
          {`${t('Common.Displayed', 'Displayed: ')} ${
            filteredCompanyGroups?.length ?? ''
          }`}
        </TextRegular14>
        <Button type="link" onClick={onCreateNewGroup}>
          {t('Company.CreateNewGroup', 'Create new group')}
        </Button>
      </Row>
      <StyledTable
        dataSource={filteredCompanyGroups}
        columns={columns}
        pagination={false}
        loading={isFetching}
        rowKey="id"
        style={{ paddingBottom: 50 }}
      />
    </>
  );
};

export default CompanyGroupsTable;
